import { useState } from 'react';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { WBButton, WBTypography, WBView } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { useFirmEntityUsers } from '../../../../../hooks/useFirmEntityUsers/useFirmEntityUsers';
import { SearchClientInput } from '../../../../SearchClientInput/SearchClientInput';
import { useDebounceValue } from 'usehooks-ts';
import { EntityUser } from '@admiin-com/ds-graphql';
import SimpleDrawDlg from '../../../../../../components/SimpleDrawDlg/SimpleDrawDlg';
import { ClientsList } from './ClientsList';
import { useLinkEntity } from '../../hooks/useLinkEntity';

export interface LinkEntityModalProps {
  open: boolean;
  onClose: () => void;
  onLinkEntity?: (entityUser: EntityUser) => void;
  clientUser: EntityUser;
}

export function LinkEntityModal({
  open,
  onClose,
  onLinkEntity,
  clientUser,
}: LinkEntityModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(searchQuery, 300);
  const [selectedEntityUser, setSelectedEntityUser] =
    useState<EntityUser | null>(null);

  const { entityUsers, total, loading, handleLoadMore } = useFirmEntityUsers({
    searchName: debouncedQuery,
    limit: 20,
  });

  const handleRowClick = (entityUser: EntityUser) => {
    setSelectedEntityUser(entityUser);
  };
  const { handleLinkEntity, loading: linkLoading } = useLinkEntity(clientUser);

  const handleLink = async () => {
    if (selectedEntityUser) {
      const updatedEntityUser = await handleLinkEntity(
        selectedEntityUser,
        clientUser
      );
      onClose();
      setSelectedEntityUser(null);
      setSearchQuery('');
      if (onLinkEntity) {
        if (updatedEntityUser) {
          onLinkEntity(updatedEntityUser);
        }
      }
    }
  };

  const handleCancel = () => {
    onClose();
    setSelectedEntityUser(null);
    setSearchQuery('');
  };

  return (
    <SimpleDrawDlg
      open={open}
      handleClose={handleCancel}
      maxWidth="lg"
      fullWidth
      noPadding
    >
      <DialogTitle variant="h3">
        {t('linkEntity', { ns: 'clients' })}
      </DialogTitle>

      <DialogContent>
        <WBView gap={2}>
          <WBView>
            <WBTypography variant="body2" color="text.secondary" mb={2}>
              {t('linkEntityDescription', {
                ns: 'clients',
                defaultValue:
                  'Search and select an entity to link with this client. Linked entities will share the same client group code.',
              })}
            </WBTypography>

            <SearchClientInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('searchClient', { ns: 'clients' })}
            />
          </WBView>

          <WBView maxHeight={'500px'}>
            <WBTypography mb={2} fontSize="16px" fontWeight="medium">
              {loading && !total
                ? '\u00A0'
                : `${total} ${t('clients', { ns: 'common' })}`}
            </WBTypography>

            <ClientsList
              data={entityUsers}
              loading={loading}
              onNextPage={handleLoadMore}
              onRowClick={handleRowClick}
              selectedEntityUser={selectedEntityUser}
              clientUser={clientUser}
            />
          </WBView>
        </WBView>
      </DialogContent>

      <DialogActions>
        <WBButton variant="outlined" onClick={handleCancel}>
          {t('cancel', { ns: 'common' })}
        </WBButton>
        <WBButton
          onClick={handleLink}
          disabled={!selectedEntityUser}
          loading={linkLoading}
          variant="contained"
        >
          {t('linkEntity', { ns: 'clients' })}
        </WBButton>
      </DialogActions>
    </SimpleDrawDlg>
  );
}
